import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback } from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { buildStyledHtmlFromRange } from '@/lib/shapes/shape-text-clipboard'
import type { ShapeTextCharRunMap, ShapeTextFonts } from '../types/shape-text-layout-state'

/** Copy, cut, select-all and delete for this frame's own (in-frame) selection. */
export function useShapeTextCommands({
  textareaRef,
  charRunMap,
  paragraphs,
  fontsRef,
  pendingRunStyleRef,
  setCaretIndex,
  setSelectionEndIndex,
  setSelectionForward,
}: {
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>
  charRunMap: ShapeTextCharRunMap
  paragraphs: Paragraph[]
  fontsRef: MutableRefObject<ShapeTextFonts>
  pendingRunStyleRef: MutableRefObject<TextRunStyleUpdate | null>
  setCaretIndex: Dispatch<SetStateAction<number>>
  setSelectionEndIndex: Dispatch<SetStateAction<number>>
  setSelectionForward: Dispatch<SetStateAction<boolean>>
}) {
  const execCopy = useCallback(() => {
    const ta = textareaRef.current

    if (!ta || ta.selectionStart === ta.selectionEnd) return
    const { selectionStart, selectionEnd } = ta
    const plain = ta.value.substring(selectionStart, selectionEnd)
    // `fontsRef` so the outgoing `text/html` carries real font-family/weight/style, not the
    // `sans-serif/400/normal` placeholder an omitted lookup emits (WMPCM-9199). larry→larry never
    // noticed — it reads the `data-*` attributes — but every external target did.
    // `paragraphs` carries the source paragraphs' own alignment/spacing/indentation onto the
    // clipboard (WMPCM-10131 Issue 2). Only run-level style used to travel, so a centred
    // paragraph pasted left-aligned — the paste fell back to the destination's settings.
    const html = buildStyledHtmlFromRange(
      ta.value,
      charRunMap,
      selectionStart,
      selectionEnd,
      fontsRef.current ?? undefined,
      paragraphs
    )

    void navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plain], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      }),
    ])
  }, [charRunMap, paragraphs, fontsRef, textareaRef])

  const execCut = useCallback(() => {
    const ta = textareaRef.current

    if (!ta || ta.selectionStart === ta.selectionEnd) return
    // Copy first; the deletion via execCommand fires onChange → handleTextChange → undo push.
    execCopy()
    document.execCommand('insertText', false, '')
  }, [execCopy, textareaRef])

  const execSelectAll = useCallback(() => {
    const ta = textareaRef.current

    if (!ta) return
    // `preventScroll` for the same reason every other focus call in this feature passes it: the
    // textarea is `opacity-0`, so a plain `focus()` scrolls the canvas viewport to it.
    ta.focus({ preventScroll: true })
    ta.setSelectionRange(0, ta.value.length, 'forward')
    // Mirrored into state, exactly as the multi-click path does (`use-shape-text-selection.ts`) and
    // for a sharper reason (WMPCM-6867). The visible highlight is painted from `caretIndex`/
    // `selectionEndIndex`, and the only thing that would otherwise write them is
    // `syncSelectionFromTextarea` via React's `onSelect` — a SYNTHESIZED event derived from
    // document `selectionchange`, which React abandons once `document.activeElement` has moved.
    // The keyboard route happens to get a follow-up `keyup` on the textarea; the context-menu and
    // menubar routes produce no key event at all and hand focus back to the menu trigger, and
    // older Firefox/Safari never fired `selectionchange` for text controls — so on those the
    // selection was set but never drawn. Idempotent, not a race: a later sync recomputes the same
    // pair, because `layoutShapeTextContent` pads carets to `text.length + 1` over this same
    // string, making its clamp a no-op for a full-length range.
    setCaretIndex(0)
    setSelectionEndIndex(ta.value.length)
    setSelectionForward(true)
    // A range replaces the caret, so a style staged for the next typed character is stale — same
    // clear `handlePointerDown` does.
    pendingRunStyleRef.current = null
  }, [pendingRunStyleRef, setCaretIndex, setSelectionEndIndex, setSelectionForward, textareaRef])

  /**
   * Delete/Backspace routed in from a canvas-level handler, for the case where the edit session is
   * still live but focus has moved to a `[data-text-edit-keepalive]` control (the Type panel root,
   * the menubar dropdown) — `use-shape-text-editing.ts`'s blur handler keeps the session open
   * there. Without this the canvas keydown handler falls through to the object delete and removes
   * the very shape being edited (WMPCM-7044).
   *
   * A `<textarea>` keeps `selectionStart`/`selectionEnd` across blur, so unlike the contentEditable
   * path there is no stale-range hazard — refocusing restores exactly the caret the user sees.
   * Deletes via `execCommand` for the same reason `execCut` does: it routes through the controlled
   * textarea's `onChange` → `handleTextChange` → `applyEditToParagraphs`, which is what pushes undo
   * and rebuilds `charRunMap`. A direct value write would bypass both.
   *
   * Returns whether text was actually removed — decided by comparing the textarea's value across
   * the call, NOT by `execCommand`'s own return value, which per spec reports whether the command
   * is supported and enabled rather than whether it changed anything. At either end of the text
   * with a collapsed caret there is nothing to remove, and the caller
   * (`deleteSelectionForActiveMode` in `center-canvas/hooks/use-text-edit-routing.ts`) uses a `false` here to hand focus back
   * to the textarea so the user's next press deletes natively — the silent no-op WMPCM-7044 TC-04
   * asks for. Reading `ta.value` is safe synchronously: `execCommand` mutates the DOM node before
   * it returns, ahead of React's `onChange`.
   */
  const execDelete = useCallback(
    (direction: 'forward' | 'backward'): boolean => {
      const ta = textareaRef.current

      if (!ta) return false
      const active = document.activeElement

      // Never yank focus out of a panel text input (font size, tracking) mid-keystroke.
      if (active !== ta && (active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA')) {
        return false
      }

      if (active !== ta) ta.focus({ preventScroll: true })

      const valueBefore = ta.value
      const issued = document.execCommand(direction === 'forward' ? 'forwardDelete' : 'delete')

      return issued && ta.value !== valueBefore
    },
    [textareaRef]
  )

  return { execCopy, execCut, execSelectAll, execDelete }
}
